import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Extrait le chemin relatif d'une URL complète pour le stockage en BDD
 * @param fullUrl URL complète (ex: http://localhost:5555/uploads/image.jpg ou https://xxx.blob.vercel-storage.com/image.jpg)
 * @returns Chemin relatif (ex: /uploads/image.jpg ou blob/image.jpg)
 */
export function extractRelativePath(fullUrl: string): string {
  if (!fullUrl) return ''

  // Déjà un chemin relatif
  if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
    return fullUrl
  }

  try {
    const url = new URL(fullUrl)

    // Blob Vercel
    if (url.hostname.includes('blob.vercel-storage.com')) {
      const filename = url.pathname.substring(1) // Enlever le /
      return `blob/${filename}`
    }

    // URL locale
    return url.pathname
  } catch (error) {
    console.error('Erreur lors de l\'extraction du chemin:', error)
    return fullUrl
  }
}

/**
 * Convertit un tableau d'URLs complètes en chemins relatifs
 */
export function extractRelativePaths(fullUrls: string[]): string[] {
  return fullUrls.map(url => extractRelativePath(url))
}

/**
 * Construit l'URL complète d'une image à partir d'un chemin relatif
 * @param relativePath Chemin relatif (ex: /uploads/image.jpg ou blob/image.jpg)
 * @returns URL complète selon l'environnement
 */
export function buildFullImageUrl(relativePath: string | undefined | null): string | null {
  if (!relativePath || typeof relativePath !== 'string' || relativePath.trim() === '') {
    return null
  }

  const trimmedPath = relativePath.trim()

  // Si c'est déjà une URL complète, la retourner telle quelle
  if (trimmedPath.startsWith('http://') || trimmedPath.startsWith('https://')) {
    return trimmedPath
  }

  // Vérifier si c'est un fichier Vercel Blob Storage
  if (trimmedPath.startsWith('blob/')) {
    const blobUrl = process.env.NEXT_PUBLIC_BLOB_URL
    if (blobUrl) {
      const filename = trimmedPath.substring(5) // Enlever 'blob/'
      return `${blobUrl}/${filename}`
    }
  }

  // URL locale - utiliser l'API URL
  const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:5000'
  const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl
  const path = trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`

  return `${baseUrl}${path}`
}

/**
 * Convertit un tableau de chemins relatifs en URLs complètes
 */
export function buildFullImageUrls(relativePaths: string[]): (string | null)[] {
  return relativePaths.map(path => buildFullImageUrl(path))
}

/**
 * Formate un nombre avec des espaces comme séparateurs de milliers
 * @param value Nombre à formater
 * @param decimals Nombre de décimales (par défaut 0)
 * @returns Nombre formaté (ex: 1234567 -> "1 234 567")
 */
export function formatNumber(value: number | string | undefined | null, decimals: number = 0): string {
  if (value === undefined || value === null || value === '') {
    return '0'
  }

  const num = typeof value === 'string' ? parseFloat(value) : value

  if (isNaN(num)) {
    return '0'
  }

  return num.toLocaleString('fr-FR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

/**
 * Formate un prix en FCFA avec des espaces comme séparateurs
 * @param value Prix à formater
 * @returns Prix formaté (ex: 1234567 -> "1 234 567 FCFA")
 */
export function formatPrice(value: number | string | undefined | null): string {
  return `${formatNumber(value)} FCFA`
}


